import {
  Box,
  Card,
  Divider,
  Tooltip,
  Typography,
  useMediaQuery,
  Grid2 as Grid,
  TextField,
  IconButton,
} from "@mui/material";
import { EditIcon, TrashIcon } from "../../icons";
import { Omnitoken } from "../../../types";
import { CARD_LOGOS } from "../../../constants";
import { useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import { RiBankFill } from "react-icons/ri";
import { TokenType } from "../../../enums";

type CardInfoTileProps = {
  data: Omnitoken;
  handleDelete?: (id: any) => any;
  handleLabelUpdate?: (id: any, newLabel: string) => any;
};

export const CardInfoTile = ({
  data,
  handleDelete,
  handleLabelUpdate,
}: CardInfoTileProps) => {
  const {
    id,
    tokenType,
    cardLast4,
    expirationMonth,
    expirationYear,
    cardLogo,
    cardLabel,
  } = data;

  const isSmallScreen = useMediaQuery("(max-width: 600px)");
  const isMediumScreen = useMediaQuery("(max-width: 1200px)");
  const [isEditMode, setIsEditMode] = useState(false);
  const [newLabel, setNewLabel] = useState(cardLabel || "");
  const isAch = tokenType === TokenType.Ach;
  const handleConfirmEdit = () => {
    setIsEditMode(false);
    if (newLabel !== cardLabel && handleLabelUpdate) {
      handleLabelUpdate(id, newLabel);
    }
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setNewLabel(cardLabel || "");
  };
  return (
    <Card
      sx={{
        paddingX: "0.75rem",
        paddingTop: "0.5rem",
        paddingBottom: "0.9rem",
        boxShadow: "0px 0px 4px 0px #00000040",
        borderRadius: "10px",
        minHeight: isSmallScreen ? "190px" : "145px",
        ":hover": {
          boxShadow: "0px 0px 8px 0px #00000040",
        },
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        {isEditMode ? (
          <TextField
            defaultValue={cardLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            size="small"
            sx={{
              paddingBottom: "0.3rem",
              fontWeight: "700",
              fontSize: isSmallScreen ? "13px" : "15px",
              width: "90%",
              whiteSpace: "nowrap",
              overflow: "hidden",
            }}
          />
        ) : (
          <Typography
            sx={{
              paddingBottom: "0.3rem",
              fontWeight: "700",
              fontSize: isSmallScreen ? "13px" : "15px",
              width: "90%",
              whiteSpace: "nowrap",
              overflow: "hidden",
            }}
          >
            <Tooltip title={cardLabel} arrow placement="top">
              <div style={{ width: "fit-content" }}>{`${
                cardLabel || cardLogo
              }`}</div>
            </Tooltip>
          </Typography>
        )}

        {isEditMode ? (
          <>
            <Box
              onClick={(event) => {
                event.stopPropagation();
                handleConfirmEdit();
              }}
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                ":hover": {
                  cursor: "pointer",
                },
              }}
            >
              <Tooltip title={"Save Changes"} arrow placement="top">
                <IconButton sx={{ padding: 0.2 }}>
                  <CheckIcon />
                </IconButton>
              </Tooltip>
            </Box>
            <Box
              onClick={(event) => {
                event.stopPropagation();
                handleCancelEdit();
              }}
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                ":hover": {
                  cursor: "pointer",
                },
              }}
            >
              <Tooltip title={"Discard"} arrow placement="top">
                <IconButton sx={{ padding: 0.1 }}>
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </>
        ) : (
          <>
            <Tooltip title={"Edit label"} arrow placement="top">
              <Box
                onClick={(event) => {
                  event.stopPropagation();
                  setIsEditMode(true);
                }}
                sx={{
                  ":hover": {
                    cursor: "pointer",
                  },
                }}
              >
                <EditIcon
                  width="20"
                  height="19"
                  style={{ marginRight: "5px" }}
                />
              </Box>
            </Tooltip>
            <Tooltip title={"Delete Card"} arrow placement="top">
              <Box
                onClick={(event) => {
                  event.stopPropagation();
                  handleDelete && handleDelete(id);
                }}
                sx={{
                  ":hover": {
                    cursor: "pointer",
                  },
                }}
              >
                <TrashIcon />
              </Box>
            </Tooltip>
          </>
        )}
      </Box>
      <Divider sx={{ borderBottomWidth: "1px", borderColor: "#A1A1A1" }} />
      <Grid
        container
        spacing={0.5}
        sx={{
          paddingTop: "0.5rem",
          alignItems: "center",
          textAlign: isSmallScreen ? "center" : "left",
        }}
      >
        <Grid
          size={{ xs: 12, sm: 4 }}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {isAch ? (
            <Box
              sx={{
                width: isSmallScreen || isMediumScreen ? "45px" : "60px",
                height: isSmallScreen || isMediumScreen ? "45px" : "60px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <RiBankFill size={32} color="#0a0b0b" />
            </Box>
          ) : (
            <Box
              component="img"
              src={CARD_LOGOS[cardLogo] || CARD_LOGOS["Other"]}
              sx={{
                width: isSmallScreen || isMediumScreen ? "60px" : "80px",
                height: isSmallScreen || isMediumScreen ? "45px" : "55px",
                objectFit: "contain",
                padding: "0.2rem",
              }}
              alt="logo"
            />
          )}
        </Grid>
        <Grid size={{ xs: 12, sm: 8 }}>
          <Box sx={{ overflow: "hidden" }}>
            {isAch ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <Typography
                  sx={{
                    fontSize: isSmallScreen ? "0.8rem" : "0.9rem",
                    marginTop: "10px",
                  }}
                >
                  xxxx xxxx xxxx {cardLast4}
                </Typography>

                <Typography
                  sx={{
                    fontSize: "0.95rem",
                    color: "text.secondary",
                  }}
                ></Typography>
              </Box>
            ) : (
              <>
                <Typography
                  sx={{
                    fontSize: isSmallScreen ? "0.9rem" : "1rem",
                    whiteSpace: "nowrap",
                    marginTop: "10px",
                  }}
                >
                  {"xxxx xxxx xxxx " + cardLast4}
                </Typography>

                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography sx={{ fontSize: "1rem" }}>{cardLogo}</Typography>

                  <Typography sx={{ fontSize: "1rem" }}>
                    {`${expirationMonth}/${expirationYear}`}
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        </Grid>
      </Grid>
    </Card>
  );
};
